import { ShapeUtils, Vector2 } from "three";

import type { PhysicsConfig } from "./config.ts";
import type {
  PhysicsSurfaceMetadata,
  TileColliderSummary,
} from "./contracts.ts";
import type { RapierModule } from "./rapierRuntime.ts";
import {
  authoredBuildingPassagesForBuilding,
  BUILDING_PASSAGE_LINING_DEPTH_BELOW_BASE_M,
  BUILDING_PASSAGE_LINING_THICKNESS_M,
  isSuppressedAuthoredGenericStructureId,
  isSuppressedGelezinioVilkoGenericStructureSegment,
  type AuthoredBuildingPassageSpec,
} from "../config/buildingPassages.ts";
import {
  authoredLandmarkCollisionForBuilding,
  authoredLandmarkTerrainCutoutContains,
  authoredLandmarkTerrainCutoutsIntersecting,
  type AuthoredLandmarkCollider,
  type AuthoredLandmarkCollisionSpec,
  type AuthoredLandmarkTerrainCutout,
  VILNIUS_AUTHORED_LANDMARK_TERRAIN_CUTOUT_GRID_M,
} from "../config/landmarks.ts";
import {
  modularCityStandaloneCollisionsForTile,
  type ModularCityStandaloneCollisionSpec,
} from "../config/modularCityAssets.ts";
import type { PolygonWithHoles } from "../world/geometry.ts";
import {
  buildingPassageSpanWithinFootprint,
  passagesOpeningFootprintRing,
  wallOpeningIntervalsForPassages,
} from "../world/buildings/passages.ts";
import type {
  BuildingFootprintCollisionSpec,
  RuntimeWorldTilePayloadV2,
} from "../world/schema/runtimeTile.ts";
import { sampleTerrainTileData } from "../world/terrain.ts";
import {
  createStreetWalkableColliderMesh,
  streetSurfaceFamilyForTile,
  terrainWithAuthoredStreetEarthworks,
} from "../world/streets.ts";
import { TREE_RENDER_CONFIG } from "../config/trees.ts";
import {
  staticParkedVehicleCollisionsForTile,
  type ParkedVehicleCollisionSpec,
} from "../config/vehicles.ts";
import type {
  AuthoredBridgeCollisionSpec,
} from "../world/bridgeCollisions.ts";
import { vegetationInstancesForTile } from "../world/municipalTrees.ts";
import { terrainWithRuntimeWaterCarving } from "../world/water.ts";

type RapierCollider = import("@dimforge/rapier3d").Collider;
type RapierWorld = import("@dimforge/rapier3d").World;

export interface AttachedTileColliders {
  tileId: string;
  colliders: readonly RapierCollider[];
  summary: TileColliderSummary;
}

export function createTileColliders(
  rapier: RapierModule,
  world: RapierWorld,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  config: Readonly<PhysicsConfig>,
  bridgeCollisions: readonly AuthoredBridgeCollisionSpec[],
  registerSurface: (
    collider: RapierCollider,
    surface: PhysicsSurfaceMetadata,
  ) => void,
  unregisterSurface: (collider: RapierCollider) => void,
): AttachedTileColliders {
  const colliders: RapierCollider[] = [];
  let provisionalBuildingHeights = 0;
  let sidewalkColliderCount = 0;
  let urbanStructureColliderCount = 0;
  const parkedVehicles = staticParkedVehicleCollisionsForTile(tile.id);
  const modularCityStandaloneCollisions =
    modularCityStandaloneCollisionsForTile(tile.id);
  const streetTerrain = terrainWithAuthoredStreetEarthworks(
    tile.terrain,
    tile.origin,
  );
  const collisionTerrain = terrainWithRuntimeWaterCarving(
    tile.origin,
    streetTerrain,
    tile.waterLines,
    tile.transportLines,
  );
  const collisionTile = collisionTerrain === tile.terrain
    ? tile
    : { ...tile, terrain: collisionTerrain };
  try {
    const terrain = world.createCollider(
      createTerrainColliderDesc(rapier, collisionTile, config),
    );
    colliders.push(terrain);
    registerSurface(terrain, {
      colliderId: `terrain:${tile.id}`,
      surfaceId: `terrain:${tile.id}`,
      classification: "terrain",
      tileId: tile.id,
    });

    const streetWalkable = createStreetWalkableColliderMesh(
      tile.transportLines,
      collisionTerrain,
      streetSurfaceFamilyForTile(tile.origin, tile.sizeM),
      { tileOrigin: tile.origin },
    );
    if (streetWalkable !== null) {
      const sidewalks = world.createCollider(
        rapier.ColliderDesc.trimesh(
          streetWalkable.vertices,
          streetWalkable.indices,
          rapier.TriMeshFlags.FIX_INTERNAL_EDGES,
        )
          .setTranslation(tile.origin.x, 0, tile.origin.z)
          .setFriction(config.terrainFriction),
      );
      colliders.push(sidewalks);
      sidewalkColliderCount = 1;
      registerSurface(sidewalks, {
        colliderId: `street-sidewalks:${tile.id}`,
        surfaceId: `sidewalk:${tile.id}`,
        classification: "street",
        tileId: tile.id,
      });
    }

    const urbanStructures = createUrbanStructureColliderDesc(
      rapier,
      collisionTile,
      config,
    );
    if (urbanStructures !== null) {
      const collider = world.createCollider(urbanStructures);
      colliders.push(collider);
      urbanStructureColliderCount = 1;
      registerSurface(collider, {
        colliderId: `urban-structures:${tile.id}`,
        surfaceId: `urban-structures:${tile.id}`,
        classification: "building",
        tileId: tile.id,
        sourceFeatureId: "osm-urban-structures",
      });
    }

    for (const tree of vegetationInstancesForTile(tile, collisionTerrain)) {
      const trunkHeightM =
        tree.heightM * TREE_RENDER_CONFIG.trunkHeightRatio;
      const trunkRadiusM =
        tree.crownRadiusM * TREE_RENDER_CONFIG.trunkRadiusToCrownRatio;
      const collider = world.createCollider(
        rapier.ColliderDesc.cylinder(trunkHeightM / 2, trunkRadiusM)
          .setTranslation(
            tile.origin.x + tree.position.x,
            tree.baseElevationM + trunkHeightM / 2,
            tile.origin.z + tree.position.z,
          )
          .setFriction(config.buildingFriction),
      );
      colliders.push(collider);
      registerSurface(collider, {
        colliderId: `tree:${tile.id}:${tree.id}`,
        surfaceId: `tree:${tile.id}:${tree.id}`,
        classification: "vegetation",
        tileId: tile.id,
        sourceFeatureId: tree.sourceFeatureId,
      });
    }

    for (const vehicle of parkedVehicles) {
      const collider = world.createCollider(
        createParkedVehicleColliderDesc(rapier, vehicle, config),
      );
      colliders.push(collider);
      registerSurface(collider, {
        colliderId: `vehicle:${vehicle.id}`,
        surfaceId: `vehicle:${vehicle.id}`,
        classification: "building",
        tileId: tile.id,
        sourceFeatureId: vehicle.id,
      });
    }

    for (const asset of modularCityStandaloneCollisions) {
      const collider = world.createCollider(
        createModularCityStandaloneColliderDesc(rapier, asset, config),
      );
      colliders.push(collider);
      registerSurface(collider, {
        colliderId: `modular-city:${asset.id}`,
        surfaceId: `modular-city:${asset.id}`,
        classification: "building",
        tileId: tile.id,
        sourceFeatureId: asset.id,
      });
    }

    for (const bridge of bridgeCollisions) {
      const collider = world.createCollider(
        createBridgeColliderDesc(rapier, bridge, config),
      );
      colliders.push(collider);
      registerSurface(collider, {
        colliderId: `bridge:${bridge.id}:deck`,
        surfaceId: `bridge:${bridge.id}`,
        classification: "street",
        tileId: tile.id,
        sourceFeatureId: bridge.id,
      });
    }

    for (const building of tile.collision.buildingFootprints) {
      const authored = authoredLandmarkCollisionForBuilding(
        tile.id,
        building.buildingId,
      );
      if (authored !== null) {
        const authoredColliders = createAuthoredLandmarkColliders(
          rapier,
          world,
          authored,
          config,
        );
        for (const collider of authoredColliders) {
          colliders.push(collider);
          registerSurface(collider, {
            // Every authored primitive keeps the canonical building ID so
            // contacts and camera fading continue to target one landmark.
            colliderId: `building:${tile.id}:${building.buildingId}`,
            surfaceId: `building:${tile.id}:${building.buildingId}`,
            classification: "building",
            tileId: tile.id,
            sourceFeatureId: building.buildingId,
          });
        }
        continue;
      }
      const passages = authoredBuildingPassagesForBuilding(
        tile.id,
        building.buildingId,
      );
      if (passages.length > 0) {
        const heightM = building.heightM ??
          config.provisionalBuildingColliderHeightM;
        if (building.heightM === undefined) {
          provisionalBuildingHeights += 1;
        }
        const passageColliders = createBuildingPassageColliders(
          rapier,
          world,
          building,
          heightM,
          tile.origin,
          passages,
          config,
        );
        for (const collider of passageColliders) {
          colliders.push(collider);
          registerSurface(collider, {
            colliderId: `building:${tile.id}:${building.buildingId}`,
            surfaceId: `building:${tile.id}:${building.buildingId}`,
            classification: "building",
            tileId: tile.id,
            sourceFeatureId: building.buildingId,
          });
        }
        continue;
      }
      const heightM = building.heightM ??
        config.provisionalBuildingColliderHeightM;
      if (building.heightM === undefined) {
        provisionalBuildingHeights += 1;
      }
      const collider = world.createCollider(
        createBuildingColliderDesc(
          rapier,
          building.footprint,
          building.baseElevationM,
          heightM,
          tile.origin,
          config,
        ),
      );
      colliders.push(collider);
      registerSurface(collider, {
        colliderId: `building:${tile.id}:${building.buildingId}`,
        surfaceId: `building:${tile.id}:${building.buildingId}`,
        classification: "building",
        tileId: tile.id,
        sourceFeatureId: building.buildingId,
      });
    }
  } catch (error) {
    for (const collider of colliders) {
      unregisterSurface(collider);
      world.removeCollider(collider, false);
    }
    throw error;
  }

  return {
    tileId: tile.id,
    colliders,
    summary: {
      tileId: tile.id,
      terrainColliderCount: 1,
      sidewalkColliderCount,
      buildingColliderCount: tile.collision.buildingFootprints.length,
      treeColliderCount: tile.vegetation?.length ?? 0,
      staticPropColliderCount:
        parkedVehicles.length + urbanStructureColliderCount +
        modularCityStandaloneCollisions.length,
      bridgeColliderCount: bridgeCollisions.length,
      usedProvisionalBuildingHeights: provisionalBuildingHeights,
    },
  };
}

function createUrbanStructureColliderDesc(
  rapier: RapierModule,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  config: Readonly<PhysicsConfig>,
) {
  const vertices: number[] = [];
  const indices: number[] = [];
  for (const feature of tile.structureLines ?? []) {
      if (
        feature.classCode === "steps" ||
        feature.classCode === "trolley_wire" ||
        feature.classCode === "parcel_boundary" ||
        feature.classCode.startsWith("parking_lane:") ||
        feature.classCode.startsWith("orthophoto_parking_row:") ||
        isSuppressedAuthoredGenericStructureId(feature.id)
      ) {
      continue;
    }
    const dimensions = structureColliderDimensions(feature.classCode);
    const widthM = Math.min(
      2.5,
      Math.max(0.08, feature.widthM ?? dimensions.widthM),
    );
    const heightM = Math.min(
      12,
      Math.max(0.25, feature.heightM ?? dimensions.heightM),
    );
    for (let pointIndex = 1; pointIndex < feature.points.length; pointIndex += 1) {
      if (
        isSuppressedGelezinioVilkoGenericStructureSegment(
          feature.id,
          pointIndex - 1,
        )
      ) {
        continue;
      }
      const from = feature.points[pointIndex - 1];
      const to = feature.points[pointIndex];
      if (from === undefined || to === undefined) {
        continue;
      }
      appendStructureColliderPrism(
        vertices,
        indices,
        from,
        to,
        tile,
        widthM,
        heightM,
      );
    }
  }
  if (vertices.length === 0 || indices.length === 0) {
    return null;
  }
  return rapier.ColliderDesc.trimesh(
    new Float32Array(vertices),
    new Uint32Array(indices),
    rapier.TriMeshFlags.FIX_INTERNAL_EDGES,
  )
    .setTranslation(tile.origin.x, 0, tile.origin.z)
    .setFriction(config.buildingFriction);
}

function appendStructureColliderPrism(
  vertices: number[],
  indices: number[],
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  widthM: number,
  heightM: number,
): void {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const lengthM = Math.hypot(dx, dz);
  if (lengthM < 0.02) {
    return;
  }
  const nx = -dz / lengthM * widthM / 2;
  const nz = dx / lengthM * widthM / 2;
  const fromBaseY = structureTerrainHeight(tile, from.x, from.z) - 0.08;
  const toBaseY = structureTerrainHeight(tile, to.x, to.z) - 0.08;
  const base = vertices.length / 3;
  vertices.push(
    from.x + nx, fromBaseY, from.z + nz,
    from.x - nx, fromBaseY, from.z - nz,
    to.x + nx, toBaseY, to.z + nz,
    to.x - nx, toBaseY, to.z - nz,
    from.x + nx, fromBaseY + heightM, from.z + nz,
    from.x - nx, fromBaseY + heightM, from.z - nz,
    to.x + nx, toBaseY + heightM, to.z + nz,
    to.x - nx, toBaseY + heightM, to.z - nz,
  );
  indices.push(
    base, base + 2, base + 4, base + 2, base + 6, base + 4,
    base + 1, base + 5, base + 3, base + 3, base + 5, base + 7,
    base + 4, base + 6, base + 5, base + 5, base + 6, base + 7,
    base, base + 4, base + 1, base + 1, base + 4, base + 5,
    base + 2, base + 3, base + 6, base + 3, base + 7, base + 6,
  );
}

function structureTerrainHeight(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  x: number,
  z: number,
): number {
  const maximum = tile.terrain.spacingM * (tile.terrain.samplesPerSide - 1);
  return sampleTerrainTileData(
    tile.terrain,
    { x: 0, z: 0 },
    {
      x: Math.min(maximum, Math.max(0, x)),
      z: Math.min(maximum, Math.max(0, z)),
    },
  ) ?? 0;
}

function structureColliderDimensions(classCode: string): Readonly<{
  widthM: number;
  heightM: number;
}> {
  switch (classCode) {
    case "fence":
      return { widthM: 0.12, heightM: 1.35 };
    case "retaining_wall":
      return { widthM: 0.45, heightM: 1.5 };
    case "cliff":
      return { widthM: 0.55, heightM: 2.2 };
    case "embankment":
      return { widthM: 0.5, heightM: 0.9 };
    default:
      return { widthM: 0.3, heightM: 1.8 };
  }
}

function createBridgeColliderDesc(
  rapier: RapierModule,
  bridge: Readonly<AuthoredBridgeCollisionSpec>,
  config: Readonly<PhysicsConfig>,
) {
  const vertices: number[] = [];
  const indices: number[] = [];
  const halfWidthM = bridge.widthM * 0.5;
  const boundaryProfiles = [
    {
      localX: bridge.segments[0]?.startLocalXM,
      surfaceWorldM: bridge.segments[0]?.startSurfaceWorldM,
      depthM: bridge.segments[0]?.depthM,
    },
    ...bridge.segments.map((segment) => ({
      localX: segment.endLocalXM,
      surfaceWorldM: segment.endSurfaceWorldM,
      depthM: segment.depthM,
    })),
  ];
  for (const boundary of boundaryProfiles) {
    if (
      boundary.localX === undefined ||
      boundary.surfaceWorldM === undefined ||
      boundary.depthM === undefined
    ) {
      throw new Error(`Bridge ${bridge.id} has an incomplete collision profile`);
    }
    vertices.push(
      boundary.localX,
      boundary.surfaceWorldM,
      -halfWidthM,
      boundary.localX,
      boundary.surfaceWorldM,
      halfWidthM,
      boundary.localX,
      boundary.surfaceWorldM - boundary.depthM,
      -halfWidthM,
      boundary.localX,
      boundary.surfaceWorldM - boundary.depthM,
      halfWidthM,
    );
  }
  for (let index = 0; index < boundaryProfiles.length - 1; index += 1) {
    const current = index * 4;
    const next = (index + 1) * 4;
    indices.push(
      current,
      current + 1,
      next,
      current + 1,
      next + 1,
      next,
      current + 2,
      next + 2,
      current + 3,
      current + 3,
      next + 2,
      next + 3,
      current,
      next,
      current + 2,
      next,
      next + 2,
      current + 2,
      current + 1,
      current + 3,
      next + 1,
      next + 1,
      current + 3,
      next + 3,
    );
  }
  const final = (boundaryProfiles.length - 1) * 4;
  indices.push(
    0,
    2,
    1,
    1,
    2,
    3,
    final,
    final + 1,
    final + 2,
    final + 1,
    final + 3,
    final + 2,
  );
  const halfYaw = bridge.rotationYRadians * 0.5;
  return rapier.ColliderDesc.trimesh(
    new Float32Array(vertices),
    new Uint32Array(indices),
    rapier.TriMeshFlags.FIX_INTERNAL_EDGES,
  )
    .setTranslation(bridge.position.x, 0, bridge.position.z)
    .setRotation({
      x: 0,
      y: Math.sin(halfYaw),
      z: 0,
      w: Math.cos(halfYaw),
    })
    .setFriction(config.terrainFriction);
}

function createBuildingPassageColliders(
  rapier: RapierModule,
  world: RapierWorld,
  building: Readonly<BuildingFootprintCollisionSpec>,
  heightM: number,
  tileOrigin: Readonly<{ x: number; z: number }>,
  passages: readonly Readonly<AuthoredBuildingPassageSpec>[],
  config: Readonly<PhysicsConfig>,
): readonly RapierCollider[] {
  const colliders: RapierCollider[] = [];
  try {
    const ceilingOffsetM = Math.max(
      ...passages.map((passage) =>
        passage.ceilingOffsetFromBuildingBaseM
      ),
    );
    if (heightM > ceilingOffsetM + 0.01) {
      colliders.push(world.createCollider(
        createBuildingColliderDesc(
          rapier,
          building.footprint,
          building.baseElevationM + ceilingOffsetM,
          heightM - ceilingOffsetM,
          tileOrigin,
          config,
        ),
      ));
    }
    const wallHeightM =
      ceilingOffsetM + BUILDING_PASSAGE_LINING_DEPTH_BELOW_BASE_M;
    const wallCenterY =
      building.baseElevationM -
      BUILDING_PASSAGE_LINING_DEPTH_BELOW_BASE_M +
      wallHeightM / 2;
    const rings = [
      { ring: building.footprint.outer, holeIndex: null },
      ...building.footprint.holes.map((ring, holeIndex) => ({ ring, holeIndex })),
    ];
    for (const { ring, holeIndex } of rings) {
      const closed = ring[0]?.x === ring.at(-1)?.x &&
          ring[0]?.z === ring.at(-1)?.z
        ? ring
        : [...ring, ring[0]!];
      for (let index = 1; index < closed.length; index += 1) {
        const from = closed[index - 1];
        const to = closed[index];
        if (from === undefined || to === undefined) {
          continue;
        }
        const edgeLengthM = Math.hypot(to.x - from.x, to.z - from.z);
        if (edgeLengthM <= 0.01) {
          continue;
        }
        const openings = wallOpeningIntervalsForPassages(
          from,
          to,
          passagesOpeningFootprintRing(passages, holeIndex),
        );
        let cursorM = 0;
        for (const opening of openings) {
          appendWallCollider(
            cursorM,
            opening.startM,
            from,
            to,
            edgeLengthM,
            wallHeightM,
            wallCenterY,
          );
          cursorM = opening.endM;
        }
        appendWallCollider(
          cursorM,
          edgeLengthM,
          from,
          to,
          edgeLengthM,
          wallHeightM,
          wallCenterY,
        );
      }
    }
    for (const passage of passages) {
      if (passage.supplementalSharedWall === true) {
        continue;
      }
      const span = buildingPassageSpanWithinFootprint(
        building.footprint,
        passage,
      );
      if (span === null) {
        continue;
      }
      const direction = {
        x: (span.to.x - span.from.x) / span.lengthM,
        z: (span.to.z - span.from.z) / span.lengthM,
      };
      const lateral = { x: -direction.z, z: direction.x };
      const midpoint = {
        x: (span.from.x + span.to.x) / 2,
        z: (span.from.z + span.to.z) / 2,
      };
      const yaw = Math.atan2(direction.x, direction.z);
      const halfYaw = yaw / 2;
      for (const side of [-1, 1] as const) {
        const lateralOffsetM =
          side *
          (passage.widthM / 2 +
            BUILDING_PASSAGE_LINING_THICKNESS_M / 2);
        colliders.push(world.createCollider(
          rapier.ColliderDesc.cuboid(
            BUILDING_PASSAGE_LINING_THICKNESS_M / 2,
            wallHeightM / 2,
            (span.lengthM + BUILDING_PASSAGE_LINING_THICKNESS_M) / 2,
          )
            .setTranslation(
              tileOrigin.x + midpoint.x + lateral.x * lateralOffsetM,
              wallCenterY,
              tileOrigin.z + midpoint.z + lateral.z * lateralOffsetM,
            )
            .setRotation({
              x: 0,
              y: Math.sin(halfYaw),
              z: 0,
              w: Math.cos(halfYaw),
            })
            .setFriction(config.buildingFriction),
        ));
      }
    }
    return colliders;
  } catch (error) {
    for (const collider of colliders) {
      world.removeCollider(collider, false);
    }
    throw error;
  }

  function appendWallCollider(
    startM: number,
    endM: number,
    from: Readonly<{ x: number; z: number }>,
    to: Readonly<{ x: number; z: number }>,
    edgeLengthM: number,
    wallHeightM: number,
    wallCenterY: number,
  ): void {
    const lengthM = endM - startM;
    if (lengthM <= 0.01) {
      return;
    }
    const centreRatio = ((startM + endM) / 2) / edgeLengthM;
    const centre = {
      x: from.x + (to.x - from.x) * centreRatio,
      z: from.z + (to.z - from.z) * centreRatio,
    };
    const yaw = -Math.atan2(to.z - from.z, to.x - from.x);
    const halfYaw = yaw / 2;
    colliders.push(world.createCollider(
      rapier.ColliderDesc.cuboid(
        lengthM / 2,
        wallHeightM / 2,
        BUILDING_PASSAGE_LINING_THICKNESS_M / 2,
      )
        .setTranslation(
          tileOrigin.x + centre.x,
          wallCenterY,
          tileOrigin.z + centre.z,
        )
        .setRotation({
          x: 0,
          y: Math.sin(halfYaw),
          z: 0,
          w: Math.cos(halfYaw),
        })
        .setFriction(config.buildingFriction),
    ));
  }
}

function createParkedVehicleColliderDesc(
  rapier: RapierModule,
  vehicle: Readonly<ParkedVehicleCollisionSpec>,
  config: Readonly<PhysicsConfig>,
): import("@dimforge/rapier3d").ColliderDesc {
  const halfYaw = vehicle.rotationYRadians / 2;
  return rapier.ColliderDesc.cuboid(
    vehicle.bodySizeM.x / 2,
    vehicle.bodySizeM.y / 2,
    vehicle.bodySizeM.z / 2,
  )
    .setTranslation(
      vehicle.origin.x,
      vehicle.origin.y + vehicle.bodySizeM.y / 2,
      vehicle.origin.z,
    )
    .setRotation({
      x: 0,
      y: Math.sin(halfYaw),
      z: 0,
      w: Math.cos(halfYaw),
    })
    .setFriction(config.buildingFriction);
}

function createModularCityStandaloneColliderDesc(
  rapier: RapierModule,
  asset: Readonly<ModularCityStandaloneCollisionSpec>,
  config: Readonly<PhysicsConfig>,
): import("@dimforge/rapier3d").ColliderDesc {
  const halfYaw = asset.rotationYRadians / 2;
  return rapier.ColliderDesc.cuboid(
    asset.bodySizeM.x / 2,
    asset.bodySizeM.y / 2,
    asset.bodySizeM.z / 2,
  )
    .setTranslation(
      asset.origin.x,
      asset.origin.y + asset.bodySizeM.y / 2,
      asset.origin.z,
    )
    .setRotation({
      x: 0,
      y: Math.sin(halfYaw),
      z: 0,
      w: Math.cos(halfYaw),
    })
    .setFriction(config.buildingFriction);
}

function createAuthoredLandmarkColliders(
  rapier: RapierModule,
  world: RapierWorld,
  landmark: Readonly<AuthoredLandmarkCollisionSpec>,
  config: Readonly<PhysicsConfig>,
): readonly RapierCollider[] {
  const colliders: RapierCollider[] = [];
  try {
    for (const primitive of landmark.colliders) {
      const desc = createAuthoredLandmarkColliderDesc(
        rapier,
        landmark,
        primitive,
        config,
      );
      colliders.push(world.createCollider(desc));
    }
    return colliders;
  } catch (error) {
    for (const collider of colliders) {
      world.removeCollider(collider, false);
    }
    throw error;
  }
}

function createAuthoredLandmarkColliderDesc(
  rapier: RapierModule,
  landmark: Readonly<AuthoredLandmarkCollisionSpec>,
  primitive: Readonly<AuthoredLandmarkCollider>,
  config: Readonly<PhysicsConfig>,
): import("@dimforge/rapier3d").ColliderDesc {
  const local = primitive.centerLocalM;
  const cosine = Math.cos(landmark.rotationYRadians);
  const sine = Math.sin(landmark.rotationYRadians);
  const translation = {
    x: landmark.origin.x + local.x * cosine + local.z * sine,
    y: landmark.origin.y + local.y,
    z: landmark.origin.z - local.x * sine + local.z * cosine,
  };
  const primitiveYaw = primitive.kind === "box"
    ? primitive.rotationYRadians ?? 0
    : 0;
  const halfYaw = (landmark.rotationYRadians + primitiveYaw) / 2;
  const halfPitch = primitive.kind === "box"
    ? (primitive.rotationXRadians ?? 0) / 2
    : 0;
  const sineYaw = Math.sin(halfYaw);
  const cosineYaw = Math.cos(halfYaw);
  const sinePitch = Math.sin(halfPitch);
  const cosinePitch = Math.cos(halfPitch);
  const rotation = {
    x: cosineYaw * sinePitch,
    y: sineYaw * cosinePitch,
    z: -sineYaw * sinePitch,
    w: cosineYaw * cosinePitch,
  };
  const desc = primitive.kind === "box"
    ? rapier.ColliderDesc.cuboid(
        primitive.sizeM.x / 2,
        primitive.sizeM.y / 2,
        primitive.sizeM.z / 2,
      )
    : rapier.ColliderDesc.cylinder(
        primitive.heightM / 2,
        primitive.radiusM,
      );
  return desc
    .setTranslation(translation.x, translation.y, translation.z)
    .setRotation(rotation)
    .setFriction(config.buildingFriction);
}

function createTerrainColliderDesc(
  rapier: RapierModule,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  config: Readonly<PhysicsConfig>,
): import("@dimforge/rapier3d").ColliderDesc {
  const side = tile.terrain.samplesPerSide;
  const expected = side * side;
  if (tile.terrain.heightsM.length !== expected) {
    throw new Error(
      `Tile ${tile.id} terrain expected ${expected} height samples`,
    );
  }

  const cutouts = authoredLandmarkTerrainCutoutsIntersecting(tile.bounds);
  if (cutouts.length > 0) {
    return createTerrainColliderWithCutoutsDesc(
      rapier,
      tile,
      config,
      cutouts,
    );
  }

  // Rapier's heightfield matrix is column-major. Runtime terrain rows advance
  // in +Z, so transpose the row-major tile buffer without changing axes.
  const columnMajorHeights = new Float32Array(expected);
  for (let row = 0; row < side; row += 1) {
    for (let column = 0; column < side; column += 1) {
      const height = tile.terrain.heightsM[row * side + column];
      if (height === undefined) {
        throw new Error(`Tile ${tile.id} terrain sample is missing`);
      }
      columnMajorHeights[column * side + row] = height;
    }
  }

  return rapier.ColliderDesc.heightfield(
    side - 1,
    side - 1,
    columnMajorHeights,
    { x: tile.sizeM, y: 1, z: tile.sizeM },
    rapier.HeightFieldFlags.FIX_INTERNAL_EDGES,
  )
    .setTranslation(
      tile.origin.x + tile.sizeM / 2,
      0,
      tile.origin.z + tile.sizeM / 2,
    )
    .setFriction(config.terrainFriction);
}

function createTerrainColliderWithCutoutsDesc(
  rapier: RapierModule,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  config: Readonly<PhysicsConfig>,
  cutouts: readonly AuthoredLandmarkTerrainCutout[],
): import("@dimforge/rapier3d").ColliderDesc {
  const divisions = Math.ceil(
    tile.sizeM / VILNIUS_AUTHORED_LANDMARK_TERRAIN_CUTOUT_GRID_M,
  );
  const stepM = tile.sizeM / divisions;
  const vertices = new Float32Array(
    (divisions + 1) * (divisions + 1) * 3,
  );
  const vertexIndex = (x: number, z: number) => z * (divisions + 1) + x;
  for (let z = 0; z <= divisions; z += 1) {
    for (let x = 0; x <= divisions; x += 1) {
      const localX = x * stepM;
      const localZ = z * stepM;
      const elevationM = sampleTerrainTileData(
        tile.terrain,
        tile.origin,
        { x: tile.origin.x + localX, z: tile.origin.z + localZ },
      );
      if (elevationM === null) {
        throw new Error(`Tile ${tile.id} terrain refinement left its bounds`);
      }
      const offset = vertexIndex(x, z) * 3;
      vertices[offset] = localX;
      vertices[offset + 1] = elevationM;
      vertices[offset + 2] = localZ;
    }
  }

  const indices: number[] = [];
  for (let z = 0; z < divisions; z += 1) {
    for (let x = 0; x < divisions; x += 1) {
      const center = {
        x: tile.origin.x + (x + 0.5) * stepM,
        z: tile.origin.z + (z + 0.5) * stepM,
      };
      if (cutouts.some((cutout) =>
        authoredLandmarkTerrainCutoutContains(cutout, center)
      )) {
        continue;
      }
      const northWest = vertexIndex(x, z);
      const northEast = vertexIndex(x + 1, z);
      const southWest = vertexIndex(x, z + 1);
      const southEast = vertexIndex(x + 1, z + 1);
      indices.push(
        northWest,
        southWest,
        northEast,
        northEast,
        southWest,
        southEast,
      );
    }
  }
  if (indices.length === 0) {
    throw new Error(`Tile ${tile.id} terrain cutouts removed every face`);
  }
  return rapier.ColliderDesc.trimesh(
    vertices,
    new Uint32Array(indices),
    rapier.TriMeshFlags.FIX_INTERNAL_EDGES,
  )
    .setTranslation(tile.origin.x, 0, tile.origin.z)
    .setFriction(config.terrainFriction);
}

function createBuildingColliderDesc(
  rapier: RapierModule,
  footprint: Readonly<PolygonWithHoles>,
  baseElevationM: number,
  heightM: number,
  tileOrigin: Readonly<{ x: number; z: number }>,
  config: Readonly<PhysicsConfig>,
): import("@dimforge/rapier3d").ColliderDesc {
  if (!Number.isFinite(heightM) || heightM <= 0) {
    throw new Error("Building collider height must be positive and finite");
  }
  const mesh = createFootprintPrismMesh(
    footprint,
    baseElevationM,
    heightM,
  );
  return rapier.ColliderDesc.trimesh(
    mesh.vertices,
    mesh.indices,
    rapier.TriMeshFlags.FIX_INTERNAL_EDGES,
  )
    .setTranslation(tileOrigin.x, 0, tileOrigin.z)
    .setFriction(config.buildingFriction);
}

export interface FootprintPrismMesh {
  vertices: Float32Array;
  indices: Uint32Array;
}

export function createFootprintPrismMesh(
  footprint: Readonly<PolygonWithHoles>,
  baseElevationM: number,
  heightM: number,
): FootprintPrismMesh {
  const rings = [footprint.outer, ...footprint.holes].map(stripClosingPoint);
  const outer = rings[0];
  if (outer === undefined || outer.length < 3) {
    throw new Error("Building footprint outer ring needs three points");
  }
  const holes = rings.slice(1);
  const contourVectors = outer.map((point) =>
    new Vector2(point.x, point.z)
  );
  const holeVectors = holes.map((ring) =>
    ring.map((point) => new Vector2(point.x, point.z))
  );
  const faces = ShapeUtils.triangulateShape(contourVectors, holeVectors);
  const planarVertices = rings.flat();
  if (faces.length === 0) {
    throw new Error("Building footprint could not be triangulated");
  }

  const topElevationM = baseElevationM + heightM;
  const vertices = new Float32Array(planarVertices.length * 2 * 3);
  for (const [index, point] of planarVertices.entries()) {
    writeVertex(vertices, index, point.x, baseElevationM, point.z);
    writeVertex(
      vertices,
      index + planarVertices.length,
      point.x,
      topElevationM,
      point.z,
    );
  }

  const indices: number[] = [];
  for (const face of faces) {
    const [a, b, c] = face;
    if (a === undefined || b === undefined || c === undefined) {
      throw new Error("Building triangulation returned an incomplete face");
    }
    indices.push(c, b, a);
    indices.push(
      a + planarVertices.length,
      b + planarVertices.length,
      c + planarVertices.length,
    );
  }

  let ringOffset = 0;
  for (const ring of rings) {
    for (let index = 0; index < ring.length; index += 1) {
      const next = (index + 1) % ring.length;
      const bottomA = ringOffset + index;
      const bottomB = ringOffset + next;
      const topA = bottomA + planarVertices.length;
      const topB = bottomB + planarVertices.length;
      indices.push(bottomA, bottomB, topB, bottomA, topB, topA);
    }
    ringOffset += ring.length;
  }

  return {
    vertices,
    indices: Uint32Array.from(indices),
  };
}

function stripClosingPoint(
  ring: PolygonWithHoles["outer"],
): readonly { x: number; z: number }[] {
  const first = ring[0];
  const last = ring[ring.length - 1];
  return first !== undefined &&
      last !== undefined &&
      first.x === last.x &&
      first.z === last.z
    ? ring.slice(0, -1)
    : ring;
}

function writeVertex(
  output: Float32Array,
  index: number,
  x: number,
  y: number,
  z: number,
): void {
  output[index * 3] = x;
  output[index * 3 + 1] = y;
  output[index * 3 + 2] = z;
}
